import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useState } from 'react';
import { Control } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';

import {
  DollarPercentButtonGroup,
  DollarPercentValues,
} from './DollarPercentButtonGroup';
import {
  CommonHypotheticalTransferFormPaths,
  CommonHypotheticalTransferFormShape,
  FundingPattern,
} from './estateWaterfallHypotheticalTransfers.types';
import { getTransferSourceValueHelpText } from './estateWaterfallHypotheticalTransfers.utils';

interface TransferFundingSourceProps<
  FormShape extends CommonHypotheticalTransferFormShape,
> {
  control: Control<FormShape>;
  displayKind: 'sale' | 'loan' | 'amount';
  proRataPattern: FundingPattern;
  sourceId: string;
  sourceValue: Decimal;
  assetOptions: TypeaheadSelectInputOption<string>[];
  helpTextAssetClassesAndBusinesses:
    | 'Select a seller to see available assets'
    | 'Select a source entity to see available assets';
  loading?: boolean;
}

export function TransferFundingSource<
  FormShape extends CommonHypotheticalTransferFormShape,
>({
  control,
  displayKind,
  proRataPattern,
  sourceId,
  sourceValue,
  assetOptions,
  helpTextAssetClassesAndBusinesses,
  loading = false,
}: TransferFundingSourceProps<FormShape>) {
  const [assetTypeaheadInputValue, setAssetTypeaheadInputValue] = useState('');
  const sourceValueHelpText = getTransferSourceValueHelpText(sourceValue);

  return (
    <FormAwareRadioGroup<FormShape>
      fieldName={
        'fundingKind' as const satisfies CommonHypotheticalTransferFormPaths
      }
      label={`Define ${displayKind} amount as`}
      hideLabel
      required
      control={control}
      row={false}
      options={[
        {
          label: '$ or % of current entity value',
          value: 'proRata',
          selectedOptionAdornment: (
            <Stack direction="row" spacing={1} alignItems="flex-start" py={1}>
              <DollarPercentButtonGroup
                fieldName={
                  'proRataPattern' as const satisfies CommonHypotheticalTransferFormPaths
                }
                label="Dollar or percent of current entity value"
                hideLabel
                required
                control={control}
              />
              {proRataPattern === DollarPercentValues.DOLLAR && (
                <FormAwareCurrencyInput
                  fieldName={
                    'proRataAmount' as const satisfies CommonHypotheticalTransferFormPaths
                  }
                  label="Dollar value"
                  hideLabel
                  {...sourceValueHelpText}
                  control={control}
                  required
                  isDecimalJSInput
                />
              )}
              {proRataPattern === DollarPercentValues.PERCENT && (
                <FormAwarePercentInput
                  fieldName={
                    'proRataPercent' as const satisfies CommonHypotheticalTransferFormPaths
                  }
                  label="Percent value"
                  hideLabel
                  {...sourceValueHelpText}
                  control={control}
                  isDecimalJSInput
                  required
                />
              )}
            </Stack>
          ),
        },
        {
          label: 'Specific owned businesses & asset categories',
          value: 'assets',
          selectedOptionAdornment: (
            <Stack direction="row" py={1} spacing={1} alignItems="flex-start">
              <DollarPercentButtonGroup
                fieldName={
                  'assetsPattern' as const satisfies CommonHypotheticalTransferFormPaths
                }
                label="Dollar or percent input for assets"
                hideLabel
                control={control}
              />
              <Box flexGrow={1}>
                <FormAwareTypeaheadMultiSelectInput
                  inputValue={assetTypeaheadInputValue}
                  onInputChange={setAssetTypeaheadInputValue}
                  groupBy={(option) => option.groupName ?? ''}
                  fieldName={
                    'selectedAssetClassesAndBusinessIds' as const satisfies CommonHypotheticalTransferFormPaths
                  }
                  label="Asset classes and businesses"
                  placeholder="Select asset classes and businesses"
                  hideLabel
                  required
                  control={control}
                  disabled={!sourceId}
                  helpText={
                    sourceId ? undefined : helpTextAssetClassesAndBusinesses
                  }
                  options={assetOptions}
                  loading={loading}
                />
              </Box>
            </Stack>
          ),
        },
      ]}
    />
  );
}
